import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { getContent } from '../../lib/api';
import type { ContentBundle, Session } from '../../lib/types';
import { pick } from '../../lib/types';
import { timeRangeLines, timeWithZone } from '../../lib/time';
import { useT } from '../../i18n/LangProvider';

function slot(s: Session): 'morning' | 'afternoon' | 'evening' {
  if (!s.StartTime) return 'morning';
  const h = new Date(s.StartTime).getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}

export default function AgendaPage() {
  const [content, setContent] = useState<ContentBundle | null>(null);
  const { t, lang } = useT();
  useEffect(() => { getContent().then(setContent).catch(() => {}); }, []);

  const sessions = content?.sessions ?? [];
  const groups: Array<['morning' | 'afternoon' | 'evening', string]> = [
    ['morning', t('agenda.morning')], ['afternoon', t('agenda.afternoon')], ['evening', t('agenda.evening')]
  ];
  const timeLines = (s: Session): string[] => {
    if (!s.StartTime) return [t('agenda.tba')];
    if (!s.EndTime) return [timeWithZone(s.StartTime, lang)];
    return timeRangeLines(s.StartTime, s.EndTime, lang);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-20 pt-36 pb-32 grid grid-cols-12 gap-6">
      <div className="col-span-12 md:col-span-10 md:col-start-2">
        <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-xs tracking-[0.25em] uppercase text-brand-red mb-5 font-mono">{t('agenda.kicker')}</motion.p>
        <motion.h1 initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} className="font-display text-5xl md:text-7xl tracking-tight mb-4 text-ink">{t('agenda.title')}</motion.h1>
        <div className="mb-16" />

        {sessions.length === 0 && <p className="text-ink-muted">{t('agenda.soon')}</p>}

        {groups.map(([key, label]) => {
          const items = sessions.filter((s) => slot(s) === key);
          if (!items.length) return null;
          return (
            <section key={key} className="mb-14">
              <h2 className="font-display text-2xl text-ink-muted mb-4">{label}</h2>
              <div className="space-y-0">
                {items.map((s, i) => (
                  <motion.article key={s.id} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.5, delay: Math.min(i * 0.04, 0.3) }} className="grid grid-cols-12 gap-4 md:gap-8 py-6 border-t border-white/[0.07]">
                    <div className="col-span-4 md:col-span-2 text-ink text-sm tabular-nums whitespace-nowrap">
                      {timeLines(s).map((line, n) => (
                        <div key={n} className={n ? 'mt-0.5' : ''}>{line}</div>
                      ))}
                    </div>
                    <div className="col-span-8 md:col-span-10">
                      <div className="flex items-center gap-3 flex-wrap text-xs uppercase tracking-wider">
                        {s.Room && <span className="text-ink-muted">{s.Room}</span>}
                      </div>
                      <h3 className="font-display text-2xl md:text-3xl text-ink mt-2 tracking-tight">{pick(lang, s.TitleEn, s.TitleFr, s.Title)}</h3>
                      {pick(lang, s.DescriptionEn, s.DescriptionFr, s.Description) && <p className="text-ink-muted mt-3 max-w-2xl">{pick(lang, s.DescriptionEn, s.DescriptionFr, s.Description)}</p>}
                      {s.JoinUrl && (
                        <a href={s.JoinUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 mt-4 text-sm text-brand-red hover:underline">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3h7v7M10 14L21 3M21 14v7H3V3h7"/></svg>
                          {lang === 'fr' ? 'Rejoindre la séance' : 'Join the session'}
                        </a>
                      )}
                    </div>
                  </motion.article>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
